// From the contract's AuthSession to the in-memory signed-in session (FR-018, FR-025, FR-035; R-9,
// SA-AUTH-2; data-model §4). Only the name, the roles and the school identity are kept: no email,
// mobile or user id (constitution VI). Credentials are handled by the controller, never here.
import type { Api } from "@/api/types";
import { initialsOf } from "@/school-identity/initials";

import type { SchoolIdentity, ServedRole, SignInPayload } from "./types";

type AuthSession = Api["AuthSession"];

/**
 * 006 (FR-002; R2): every SCHOOL_ADMIN grant with a school, in server order, de-duplicated by id.
 */
export function schoolsOf(auth: AuthSession): SchoolIdentity[] {
  const out: SchoolIdentity[] = [];
  for (const g of auth.roles) {
    if (g.role !== "SCHOOL_ADMIN" || !g.school) continue;
    if (out.some((s) => s.id === g.school!.id)) continue;
    out.push({ id: g.school.id, name: g.school.name, logoUrl: g.school.logoUrl });
  }
  return out;
}

/** The roles this app can act on: PARENT, and SCHOOL_ADMIN only with a school. */
export function servedFromAuth(auth: AuthSession): ServedRole[] {
  const served: ServedRole[] = [];
  if (auth.roles.some((g) => g.role === "PARENT")) served.push("PARENT");
  if (schoolsOf(auth).length > 0) served.push("SCHOOL_ADMIN");
  return served;
}

export function toSignedInSession(
  auth: AuthSession,
  activeRole: ServedRole,
): SignInPayload {
  return {
    user: { displayName: auth.user.name, initials: initialsOf(auth.user.name) },
    roles: auth.roles.map((g) => g.role),
    schoolAdminSchools: schoolsOf(auth),
    activeRole,
  };
}
